import { Responsive, WidthProvider } from 'react-grid-layout'

import type { ReactElement } from 'react'

import CurrentVsStartBlock from './CurrentVsStartBlock'
import GraphBlock from './GraphBlock'
import MAUValueBlock from './MAUValueBlock'
import Top10Block from './Top10Block'
import Top5Block from './Top5Block'

import { useOkrsListQuery } from '../queries'

import BlockTitle from '~/components/Blocks/Title'
import ContentLayout from '~/components/ContentLayout'
import GridItemLayout from '~/components/GridItemLayout'
import MwMLoader from '~/components/MwMLoader'
import Section from '~/components/Section'
import SimpleGauge from '~/components/SimpleGauge'
import { COMMON_GRID_PROPS } from '~/const'
import { getQuarter } from '~/utils/home'

import './styles.scss'

const ResponsiveGridLayout = WidthProvider(Responsive)

const Layouts1 = {
  lg: [
    // Objective completion
    { h: 3.4, i: '0', w: 3, x: 0, y: 0 },
    // Current vs Last Quarter variation
    { h: 1.4, i: '1', w: 3, x: 3, y: 0 },
    // Current MAU
    { h: 1, i: '2', w: 3, x: 3, y: 1 },
    // Last Quarter MAU
    { h: 1, i: '3', w: 3, x: 3, y: 2 },
    // Top 5 apps MAU
    { h: 3.4, i: '4', w: 6, x: 6, y: 0 },
    // MAU over time
    { h: 3.2, i: '5', w: 12, x: 0, y: 3 },
  ],
  md: [
    // Objective completion
    { h: 3.4, i: '0', w: 3, x: 0, y: 0 },
    // Current vs Last Quarter variation
    { h: 1.4, i: '1', w: 3, x: 3, y: 0 },
    // Current MAU
    { h: 1, i: '2', w: 3, x: 3, y: 1 },
    // Last Quarter MAU
    { h: 1, i: '3', w: 3, x: 3, y: 2 },
    // Top 5 apps MAU
    { h: 3.4, i: '4', w: 6, x: 6, y: 0 },
    // MAU over time
    { h: 3.2, i: '5', w: 12, x: 0, y: 3 },
  ],
}

const Layouts2 = {
  lg: [
    // Top 10 variations (d-7)
    { h: 6.9, i: '0', w: 4, x: 0, y: 0 },
    // Top 10 variations (d-30)
    { h: 6.9, i: '1', w: 4, x: 4, y: 0 },
    // Top 10 variations (last quarter)
    { h: 6.9, i: '2', w: 4, x: 8, y: 0 },
  ],
  md: [
    // Top 10 variations (d-7)
    { h: 6.9, i: '0', w: 4, x: 0, y: 0 },
    // Top 10 variations (d-30)
    { h: 6.9, i: '1', w: 4, x: 4, y: 0 },
    // Top 10 variations (last quarter)
    { h: 6.9, i: '2', w: 4, x: 8, y: 0 },
  ],
}

const ObjectiveKeyResultsBlock = (): ReactElement => {
  const { data: okrsData, isLoading: okrsDataLoading } = useOkrsListQuery()

  if (okrsDataLoading) {
    return (
      <div className="objective-key-results-block">
        <MwMLoader category="item" />
      </div>
    )
  }

  return (
    <div className="objective-key-results-block">
      <ContentLayout>
        <Section
          title={
            <div className="objective-key-results-block--main-title">
              <span>Reach </span>
              <span className="highlight-colored">{okrsData?.target_string} </span>
              <span>of </span>
              <span className="highlight-bold">Monthly Active Users (MAU) </span>
              <span>on </span>
              <span className="highlight-bold">all Apps & Games</span>
            </div>
          }
        >
          <ResponsiveGridLayout {...COMMON_GRID_PROPS} isDraggable={false} isResizable={false} layouts={Layouts1}>
            <div key="0">
              <GridItemLayout className="gauge-block">
                <BlockTitle>Objective completion</BlockTitle>
                <div className="gauge-wrapper">
                  <SimpleGauge value={(okrsData?.target_completion || 0) * 100} />
                </div>
              </GridItemLayout>
            </div>
            <div key="1">
              <CurrentVsStartBlock
                title={
                  <>
                    Current <span className="grey">vs</span> Start of Q{getQuarter()}
                  </>
                }
                value={okrsData?.progression_since_quarter_start || 0}
              />
            </div>
            <div key="2">
              <MAUValueBlock isCurrentValue title="Current MAU" value={okrsData?.mau_now} />
            </div>
            <div key="3">
              <MAUValueBlock title={`Start of Q${getQuarter()} MAU`} value={okrsData?.mau_at_quarter_start} />
            </div>
            <div key="4">
              <Top5Block topApps={okrsData?.mau_now_top_apps} />
            </div>
            <div key="5">
              <GraphBlock graphData={okrsData?.monthly_data} target={okrsData?.target} title="MAU over time" />
            </div>
          </ResponsiveGridLayout>
        </Section>
        <Section
          title={
            <div className="objective-key-results-block--secondary-title">
              Top MAU variations <span className="highlight-light">for apps {'>'} 5K MAU</span>
            </div>
          }
        >
          <ResponsiveGridLayout {...COMMON_GRID_PROPS} isDraggable={false} isResizable={false} layouts={Layouts2}>
            <div key="0">
              <Top10Block
                appsData={okrsData?.mau_7d_top_apps}
                title={
                  <>
                    Current <span className="grey">vs</span> d-7
                  </>
                }
              />
            </div>
            <div key="1">
              <Top10Block
                appsData={okrsData?.mau_30d_top_apps}
                title={
                  <>
                    Current <span className="grey">vs</span> d-30
                  </>
                }
              />
            </div>
            <div key="2">
              <Top10Block
                appsData={okrsData?.mau_1q_top_apps}
                title={
                  <>
                    Current <span className="grey">vs</span> Start of Q{getQuarter()}
                  </>
                }
              />
            </div>
          </ResponsiveGridLayout>
        </Section>
      </ContentLayout>
    </div>
  )
}

export default ObjectiveKeyResultsBlock
